<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { NButton, NCard, NIcon, useThemeVars } from 'naive-ui';
import { ACTIVATION_GRANT } from '@/constants/business';
import { useAuthStore } from '@/store/modules/auth';
import { useUserStore } from '@/store/modules/user';
import { useAppStore } from '@/store/modules/app';
import { $t } from '@/locales';

const route = useRoute();
const authStore = useAuthStore();
const userStore = useUserStore();
const appStore = useAppStore();
const themeVars = useThemeVars();
const direction = computed(() => appStore.direction);
const loading = ref(false);

// This overlay is teleported outside the dashboard shell, so explicitly map
// Naive theme values onto its bounded root instead of relying on ambient vars.
const overlayThemeStyle = computed(() => ({
  '--overlay-surface': themeVars.value.cardColor,
  '--overlay-border': themeVars.value.borderColor,
  '--overlay-text-1': themeVars.value.textColor1,
  '--overlay-text-2': themeVars.value.textColor2
}));

const showOverlay = computed(() => authStore.showLoginOverlay);

// Display-only activation grant figures (backend applies the real credit).
const grantTotal = computed(() => ACTIVATION_GRANT.TOTAL_IRT.toLocaleString('en-US'));
const grantTopup = computed(() => ACTIVATION_GRANT.PHONE_TOPUP_IRT.toLocaleString('en-US'));

const routeBenefits = computed(() => {
  const routeName = route.name as string;

  // Map route names to their i18n benefit keys
  const benefitsMap: Record<string, string[]> = {
    affiliate: [
      $t('page.login.common.benefits.affiliate.a'),
      $t('page.login.common.benefits.affiliate.b'),
      $t('page.login.common.benefits.affiliate.c')
    ],
    'api-keys': [
      $t('page.login.common.benefits.api-keys.a'),
      $t('page.login.common.benefits.api-keys.b'),
      $t('page.login.common.benefits.api-keys.c')
    ],
    billing_credit: [
      $t('page.login.common.benefits.billing.a'),
      $t('page.login.common.benefits.billing.b'),
      $t('page.login.common.benefits.billing.c')
    ],
    billing_addresses: [
      $t('page.login.common.benefits.billing.a'),
      $t('page.login.common.benefits.billing.b'),
      $t('page.login.common.benefits.billing.c')
    ],
    billing_history: [
      $t('page.login.common.benefits.billing.a'),
      $t('page.login.common.benefits.billing.b'),
      $t('page.login.common.benefits.billing.c')
    ],
    billing_invoices: [
      $t('page.login.common.benefits.billing.a'),
      $t('page.login.common.benefits.billing.b'),
      $t('page.login.common.benefits.billing.c')
    ],
    billing_packages: [
      $t('page.login.common.benefits.packages.a'),
      $t('page.login.common.benefits.packages.b'),
      $t('page.login.common.benefits.packages.c')
    ],
    home: [
      $t('page.login.common.benefits.home.a'),
      $t('page.login.common.benefits.home.b'),
      $t('page.login.common.benefits.home.c')
    ],
    models: [
      $t('page.login.common.benefits.models.a'),
      $t('page.login.common.benefits.models.b'),
      $t('page.login.common.benefits.models.c')
    ],
    usage_overview: [
      $t('page.login.common.benefits.usage.a'),
      $t('page.login.common.benefits.usage.b'),
      $t('page.login.common.benefits.usage.c')
    ],
    usage_reports: [
      $t('page.login.common.benefits.usage.a'),
      $t('page.login.common.benefits.usage.b'),
      $t('page.login.common.benefits.usage.c')
    ],
    support_dashboard: [
      $t('page.login.common.benefits.support.a'),
      $t('page.login.common.benefits.support.b'),
      $t('page.login.common.benefits.support.c')
    ],
    'support_my-tickets': [
      $t('page.login.common.benefits.support.a'),
      $t('page.login.common.benefits.support.b'),
      $t('page.login.common.benefits.support.c')
    ],
    'support_create-ticket': [
      $t('page.login.common.benefits.support.a'),
      $t('page.login.common.benefits.support.b'),
      $t('page.login.common.benefits.support.c')
    ],
    'support_knowledge-base': [
      $t('page.login.common.benefits.support.a'),
      $t('page.login.common.benefits.support.b'),
      $t('page.login.common.benefits.support.c')
    ]
  };

  return benefitsMap[routeName] || [];
});

const handleLogin = () => {
  loading.value = true;
  // Store the current route for post-login redirect
  authStore.setOriginalRoute(route.fullPath);
  authStore.loginWithRedirect();
};

const handleDismiss = () => {
  authStore.setOverlayVisibility(false);
  userStore.setGuestMode(true);
  userStore.setGuestSessionStart();

  // Show guest mode banner
  authStore.setGuestBannerVisibility(true);
};

const handleBackdropClick = () => {
  // Allow dismissal by clicking backdrop
  handleDismiss();
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="showOverlay"
      class="login-overlay"
      :dir="direction"
      :style="overlayThemeStyle"
      @click.self="handleBackdropClick"
    >
      <div class="login-overlay__dialog">
        <NCard class="login-overlay__card" :bordered="false" size="large" closable @close="handleDismiss">
          <!-- Grant hero banner -->
          <section class="grant-hero" role="status" aria-live="polite">
            <span class="grant-hero__icon" aria-hidden="true">
              <NIcon size="22">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67l-.5-.68C10.96 2.54 10.05 2 9 2C7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2m-5-2c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1M9 4c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1m11 15H4v-2h16zm0-5H4V8h5.08L7 10.83L8.62 12L11 8.76l1-1.36l1 1.36L15.38 12L17 10.83L14.92 8H20z"
                  />
                </svg>
              </NIcon>
            </span>
            <div class="grant-hero__body">
              <h2 class="grant-hero__title">
                {{ $t('page.login.common.grantHeroTitle', { amount: grantTotal }) }}
              </h2>
              <p class="grant-hero__subtitle">
                {{ $t('page.login.common.grantHeroSubtitle') }}
              </p>
            </div>
          </section>

          <!-- Primary actions -->
          <div class="login-overlay__actions">
            <NButton type="primary" size="large" block :loading="loading" @click="handleLogin">
              {{ $t('page.login.common.grantCtaPrimary') }}
            </NButton>

            <NButton quaternary size="medium" block @click="handleDismiss">
              {{ $t('page.login.common.continueAsGuest') }}
            </NButton>
          </div>

          <!-- Existing email-user phone top-up note -->
          <p class="grant-topup-note">
            {{
              $t('page.login.common.grantEmailTopupNote', {
                amount: grantTopup,
                total: grantTotal
              })
            }}
          </p>

          <!-- Context-aware benefits -->
          <section v-if="routeBenefits.length > 0" class="login-benefits">
            <h4 class="login-benefits__title">
              {{ $t('page.login.common.unlockFeatures') }}
            </h4>
            <ul class="login-benefits__list">
              <li v-for="benefit in routeBenefits" :key="benefit" class="login-benefits__item">
                {{ benefit }}
              </li>
            </ul>
          </section>
        </NCard>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.login-overlay {
  /* Scoped local-alias token layer mapped to platform/Naive tokens so
     light/dark + brand follow automatically. */
  --overlay-accent: rgb(var(--primary-color));
  --overlay-accent-soft: rgb(var(--primary-color) / 0.12);
  --overlay-surface: var(--card-color, var(--n-card-color));
  --overlay-text-1: var(--text-color-1, var(--n-text-color));
  --overlay-text-2: var(--text-color-2, var(--n-text-color-2));
  --overlay-border: var(--border-color, var(--n-border-color));
  --overlay-success: rgb(var(--success-color));

  /* 4pt spacing scale */
  --overlay-space-2: 0.5rem;
  --overlay-space-3: 0.75rem;
  --overlay-space-4: 1rem;
  --overlay-space-5: 1.25rem;
  --overlay-space-6: 1.5rem;

  --overlay-radius: 8px;
  --overlay-shell-radius: 14px;
  --overlay-fs-caption: 0.75rem;
  --overlay-fs-body: 0.875rem;

  position: fixed;
  inset: 0;
  z-index: 50;
  display: grid;
  place-items: center;
  padding: max(var(--overlay-space-4), env(safe-area-inset-top)) max(var(--overlay-space-4), env(safe-area-inset-right))
    max(var(--overlay-space-4), env(safe-area-inset-bottom)) max(var(--overlay-space-4), env(safe-area-inset-left));
  overflow: auto;
  overscroll-behavior: contain;
  background: color-mix(in srgb, var(--overlay-text-1) 44%, transparent);
  backdrop-filter: blur(2px);
}

.login-overlay__dialog {
  position: relative;
  isolation: isolate;
  inline-size: min(100%, 30rem);
  min-inline-size: 0;
  max-block-size: calc(100dvh - (var(--overlay-space-4) * 2));
  margin: auto;
  overflow: hidden;
  border-radius: var(--overlay-shell-radius);
  background: var(--overlay-surface);
  box-shadow: var(--n-box-shadow);
  animation: login-dialog-enter 180ms ease-out both;
}

.login-overlay__card {
  --n-border-radius: var(--overlay-shell-radius);
  --n-color: var(--overlay-surface);

  position: relative;
  max-block-size: inherit;
  overflow: hidden;
  border: 1px solid var(--overlay-border);
  border-radius: var(--overlay-shell-radius);
  background: var(--overlay-surface);
  box-shadow: var(--n-box-shadow);
}

.login-overlay__card :deep(.n-card__content) {
  max-block-size: inherit;
  min-inline-size: 0;
  padding: var(--overlay-space-6);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

/* .login-overlay__card :deep(.n-card-header__close) {
  inset-block-start: var(--overlay-space-3);
  inset-inline-end: var(--overlay-space-3);
} */

@keyframes login-dialog-enter {
  from {
    opacity: 0;
    transform: translateY(var(--overlay-space-2)) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Grant hero banner */
.grant-hero {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: var(--overlay-space-3);
  padding: var(--overlay-space-4);
  border-radius: var(--overlay-radius);
  border: 1px solid var(--overlay-accent-soft);
  background: color-mix(in srgb, var(--primary-color-source, rgb(var(--primary-color))) 8%, var(--overlay-surface));
  border-inline-start: 3px solid var(--overlay-accent);
  margin-block-end: var(--overlay-space-4);
}

.grant-hero__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 2.5rem;
  block-size: 2.5rem;
  border-radius: 999px;
  color: var(--overlay-accent);
  background: var(--overlay-accent-soft);
}

.grant-hero__body {
  min-inline-size: 0;
}

.grant-hero__title {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--overlay-text-1);
}

.grant-hero__subtitle {
  max-inline-size: 36ch;
  margin: var(--overlay-space-2) 0 0;
  font-size: var(--overlay-fs-body);
  font-weight: 450;
  line-height: 1.6;
  color: color-mix(in srgb, var(--overlay-text-1) 72%, var(--overlay-text-2));
  text-wrap: pretty;
}

/* Actions */
.login-overlay__actions {
  display: flex;
  flex-direction: column;
  gap: var(--overlay-space-3);
  text-align: center;
}

/* Top-up note */
.grant-topup-note {
  margin: var(--overlay-space-4) 0 0;
  padding: var(--overlay-space-3);
  border-radius: var(--overlay-radius);
  background: color-mix(in srgb, var(--success-color-source, rgb(var(--success-color))) 8%, var(--overlay-surface));
  border: 1px solid color-mix(in srgb, var(--overlay-success) 24%, transparent);
  border-inline-start: 3px solid var(--overlay-success);
  font-size: var(--overlay-fs-caption);
  line-height: 1.5;
  color: var(--overlay-text-2);
  text-align: start;
}

/* Route benefits */
.login-benefits {
  margin-block-start: var(--overlay-space-4);
  padding: var(--overlay-space-3);
  border-radius: var(--overlay-radius);
  background: var(--overlay-accent-soft);
}

.login-benefits__title {
  margin: 0 0 var(--overlay-space-2);
  font-size: var(--overlay-fs-body);
  font-weight: 600;
  color: var(--overlay-accent);
  text-align: start;
}

.login-benefits__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--overlay-space-2);
}

.login-benefits__item {
  position: relative;
  padding-inline-start: var(--overlay-space-4);
  font-size: var(--overlay-fs-caption);
  line-height: 1.5;
  color: var(--overlay-text-2);
  text-align: start;
}

.login-benefits__item::before {
  content: '•';
  position: absolute;
  inset-inline-start: 0;
  color: var(--overlay-accent);
}

/* Focus ring */
.login-overlay :where(button, a, [tabindex]):focus-visible {
  outline: 2px solid var(--overlay-accent);
  outline-offset: 2px;
}

@media (max-width: 480px) {
  .login-overlay {
    place-items: end center;
    padding-inline: var(--overlay-space-2);
    padding-block-end: max(var(--overlay-space-2), env(safe-area-inset-bottom));
  }

  .login-overlay__dialog {
    inline-size: 100%;
    max-block-size: calc(100dvh - var(--overlay-space-4));
  }

  .login-overlay__card :deep(.n-card__content) {
    padding: var(--overlay-space-5) var(--overlay-space-4);
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-overlay,
  .login-overlay * {
    transition: none !important;
    animation: none !important;
  }
}
</style>
